/**
 * MultiBuy Builder - collection.js
 * Owns two things that both browse a Shopify collection's products, and
 * both render through the exact same product-card component
 * (MB.product.renderGrid/renderCard - see product.js) so there is only
 * one card, one variant selector, and one add/remove code path in the
 * whole app:
 *   1. The bottom sheet (View Collection) — a live grid of every product
 *      in the bundle's collection. Products already in the bundle show
 *      as "added" (checkmark fab); clicking any card's fab toggles it
 *      in/out of the bundle.
 *   2. The inline "Create Your Own Pack" grid (search/filter/sort + toggle).
 * Attaches to window.MB.collection
 */
(function (window, document) {
  'use strict';

  var MB = (window.MB = window.MB || {});
  var utils = MB.utils;
  var api = MB.api;
  var discount = MB.discount;
  var store = MB.store;
  var productUI = MB.product;

  var cardSelections = {}; // productId -> array of selected option values, per grid instance

  // ---------------------------------------------------------------------
  // Shared grid rendering (browse view + create-your-own)
  // ---------------------------------------------------------------------

  function toItem(product, variant, quantity) {
    return {
      productId: product.id,
      productHandle: product.handle,
      productTitle: product.title,
      productImage: product.featured_image,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      compareAtPrice: variant.compare_at_price,
      quantity: quantity || 1
    };
  }

  var GRID_PAGE_SIZE = 24;

  function skeletonGridHtml(count) {
    var card =
      '<div class="mb-skeleton-card">' +
      '<div class="mb-skeleton-card__img"></div>' +
      '<div class="mb-skeleton-card__line mb-skeleton-card__line--title"></div>' +
      '<div class="mb-skeleton-card__line mb-skeleton-card__line--sub"></div>' +
      '<div class="mb-skeleton-card__line mb-skeleton-card__line--price"></div>' +
      '</div>';
    return new Array(count + 1).join(card);
  }

  function createGridController(containerEl, opts) {
    var state = {
      bundleId: opts.bundleId,
      collectionHandle: opts.collectionHandle,
      mode: opts.mode || 'add', // 'add' (create-your-own) | 'select' (sheet browse)
      excludeSelected: !!opts.excludeSelected,
      // Frozen snapshot of ids to exclude, instead of re-deriving live
      // from ctx.items on every render - see Replace Selection below,
      // where picking a product must NOT make its card disappear.
      excludeIds: opts.excludeIds || null,
      onlySelected: !!opts.onlySelected,
      all: [],
      query: '',
      filters: {},
      sort: 'featured',
      selections: {}, // productId -> selectedOptions[]
      // Client-side "infinite scroll": the full filtered/sorted list is
      // computed as before (so search/filter/sort still run against
      // every loaded product, not just what's on screen), but only the
      // first renderLimit of it are actually painted into the DOM. A
      // sentinel node at the end of the grid, watched by an
      // IntersectionObserver, reveals more as the shopper scrolls near
      // it - this is what was making a large collection's very first
      // render slow (one huge innerHTML for every product at once).
      filteredList: [],
      renderLimit: GRID_PAGE_SIZE
    };
    var sentinelObserver = null;

    var searchCtl = MB.search.createSearchController(
      function () { return state.all; },
      function (results) {
        renderResults(applyFiltersAndSort(results));
      }
    );

    function applyFiltersAndSort(list) {
      var filtered = MB.filter.applyFilters(list, state.filters);
      filtered = MB.filter.sortProducts(filtered, state.sort);
      if (state.excludeSelected || state.onlySelected) {
        var ctx = store.getContext(state.bundleId);
        var liveSelectedIds = ctx ? ctx.items.map(function (i) { return i.productId; }) : [];
        var packIds = null;
        filtered = filtered.filter(function (p) {
          if (state.onlySelected) {
            // Default sheet view: show the pack's fixed product set, not
            // just whichever ones happen to be selected right now, so a
            // deselected card stays put (dimmed) instead of vanishing.
            packIds = ctx && ctx.packProductIds && ctx.packProductIds.length ? ctx.packProductIds : liveSelectedIds;
            return packIds.indexOf(p.id) !== -1;
          }
          var excludeIds = state.excludeIds || liveSelectedIds;
          return excludeIds.indexOf(p.id) === -1;
        });
        // Default sort only: re-order by each product's own slot in
        // packProductIds instead of leaving it in the collection's
        // natural order - otherwise a product picked via Replace
        // Selection shows up wherever it naturally falls in the whole
        // collection (often the very end) instead of staying in the
        // slot it just filled. An explicit sort choice (newest/price/
        // alphabetical) still wins, since that's a deliberate re-sort.
        if (state.onlySelected && packIds && state.sort === 'featured') {
          filtered.sort(function (a, b) {
            return packIds.indexOf(a.id) - packIds.indexOf(b.id);
          });
        }
      }
      // Keep fixed pack slots in place; browsing lists prioritize full size runs.
      return state.onlySelected ? filtered : MB.filter.stockOrderedProducts(filtered);
    }

    function paintVisible() {
      var ctx = store.getContext(state.bundleId);
      var visible = state.filteredList.slice(0, state.renderLimit);
      containerEl.innerHTML = productUI.renderGrid(visible, ctx, state.mode === 'select' ? 'select' : 'add');
      containerEl.dataset.mbContext = state.bundleId;
      // Search, sorting and pagination redraw cards from the saved pack,
      // but a replacement is only staged until the shopper confirms it.
      if (sheet.contentMode === 'replace' && sheet.bundleId === state.bundleId &&
          containerEl.closest('.mb-collection-page') && sheet.pendingReplacement) {
        setCardPendingState(sheet.pendingReplacement.toItemValue.productId, true);
      }
      setupSentinel();
    }

    function setupSentinel() {
      if (sentinelObserver) {
        sentinelObserver.disconnect();
        sentinelObserver = null;
      }
      if (state.renderLimit >= state.filteredList.length) return;
      var sentinel = document.createElement('div');
      sentinel.className = 'mb-grid-sentinel';
      containerEl.appendChild(sentinel);
      sentinelObserver = new IntersectionObserver(function (entries) {
        if (entries[0] && entries[0].isIntersecting) {
          state.renderLimit += GRID_PAGE_SIZE;
          paintVisible();
        }
      });
      sentinelObserver.observe(sentinel);
    }

    // preserveLimit: keeps however many pages the shopper has already
    // scrolled through instead of snapping back to the first page - see
    // the bundle:changed subscription below, which calls refresh(true)
    // on every add/remove. Without this, selecting a product after
    // scrolling down (past the first GRID_PAGE_SIZE items) reset the
    // grid back to just the first page on every single click, leaving
    // the shopper scrolled into now-empty space below the shrunken grid
    // - a blank/white screen until they scrolled back up manually. A
    // real query/filter/sort change still starts over at page one,
    // since that's a genuinely different result set worth seeing fresh.
    function renderResults(list, preserveLimit) {
      state.filteredList = list;
      if (!preserveLimit) state.renderLimit = GRID_PAGE_SIZE;
      paintVisible();
    }

    function refresh(preserveLimit) {
      // NOT searchCtl.setQuery here - refresh() already recomputes the
      // fully searched/filtered/sorted list synchronously on the next
      // line, so re-arming searchCtl's own 250ms debounce on every
      // filter/sort/selection change served no purpose except to fire a
      // SECOND, delayed re-render later via its callback below - one
      // that didn't know about preserveLimit and reset the page back to
      // one regardless, undoing the fix a moment after every selection
      // (the actual cause of the lingering white-screen glitch after
      // scrolling + selecting). searchCtl.setQuery is still called
      // directly from setQuery() itself, so live search-as-you-type is
      // unaffected.
      renderResults(applyFiltersAndSort(MB.search.searchProducts(state.all, state.query)), preserveLimit);
    }

    function load() {
      containerEl.innerHTML = skeletonGridHtml(8);
      return (opts.globalCatalog ? api.getAllProducts() : api.getCollectionProducts(state.collectionHandle)).then(function (products) {
        state.all = MB.filter.stockOrderedProducts(products);
        var facets = MB.filter.buildFacets(state.all);
        utils.dispatch('multibuy:grid:loaded', { bundleId: state.bundleId, facets: facets, container: containerEl });
        refresh();
        return state.all;
      });
    }

    function setQuery(q) {
      state.query = q;
      searchCtl.setQuery(q);
    }

    function setFilters(filters) {
      state.filters = filters;
      refresh();
    }

    function setSort(sortKey) {
      state.sort = sortKey;
      refresh();
    }

    // Toggled on while the sheet is in "Replace Selection" mode, so the
    // product being replaced (and every other already-selected product)
    // is hidden - the shopper can only pick a genuinely different item.
    function setExcludeSelected(value) {
      state.excludeSelected = !!value;
      refresh();
    }

    // Toggled on for the sheet's default (non-replace) view: show only
    // the products currently in the pack, not the whole collection.
    function setOnlySelected(value) {
      state.onlySelected = !!value;
      refresh();
    }

    var unsubscribe = store.on('bundle:changed', function (ctx) {
      // Re-render whenever this grid's own bundle context changes so the
      // per-card "Added" state and live discount preview always reflect
      // the current selection, not just when the sheet excludes picks.
      // preserveLimit: true - a selection toggle, not a new search/
      // filter/sort, so keep whatever page the shopper has scrolled to.
      if (ctx.id === state.bundleId) refresh(true);
    });

    function getProducts() {
      return state.all;
    }

    return {
      load: load,
      refresh: refresh,
      setQuery: setQuery,
      setFilters: setFilters,
      setSort: setSort,
      setExcludeSelected: setExcludeSelected,
      setOnlySelected: setOnlySelected,
      getProducts: getProducts,
      destroy: function () {
        unsubscribe();
        if (sentinelObserver) sentinelObserver.disconnect();
      }
    };
  }

  // ---------------------------------------------------------------------
  // Card interaction (chip selection + add/select) shared across grids
  // ---------------------------------------------------------------------

  function bindGridEvents() {
    document.addEventListener('click', function (e) {
      var card = e.target.closest('.mb-product-card');
      if (!card) return;
      var containerEl = card.closest('[data-mb-context]');
      var bundleId = containerEl ? containerEl.dataset.mbContext : null;
      var productId = card.dataset.productId;

      var chip = e.target.closest('.mb-chip[data-mb-chip-value]');
      if (chip && !chip.disabled) {
        handleChipClick(card, chip);
        return;
      }

      var replaceBtn = e.target.closest('[data-mb-action="replace-product"]');
      if (replaceBtn && bundleId) {
        handleReplaceTrigger(card, bundleId);
        return;
      }

      var action = e.target.closest('[data-mb-action="add-product"], [data-mb-action="select-product"]');
      if (action && bundleId) {
        handleCardAction(card, bundleId);
      }
    });
  }

  /** Mirrors product.js's renderCard: a real size choice only exists
   * when there's more than one value to pick from. */
  function requiresSizeChoice(product) {
    var firstOption = product.options[0];
    return !!(firstOption && firstOption.values && firstOption.values.length > 1);
  }

  /** "Replace Selection" clicked on an already-added card: remember its
   * slot and switch this grid into replace mode (selected products
   * hidden) until a replacement is picked or the shopper cancels. */
  function handleReplaceTrigger(card, bundleId) {
    var product = card._mbProduct;
    var ctx = store.getContext(bundleId);
    if (!product || !ctx) return;
    var index = -1;
    ctx.items.forEach(function (item, i) {
      if (item.productId === product.id) index = i;
    });
    if (index === -1) return;
    startReplace(bundleId, index);
  }

  function handleChipClick(card, chip) {
    var product = card._mbProduct;
    if (!product) return;
    var row = chip.closest('.mb-chip-row');
    var siblings = utils.qsa('.mb-chip', row);
    siblings.forEach(function (c) { c.classList.remove('mb-chip--selected'); });
    chip.classList.add('mb-chip--selected');

    var variant = productUI.pickVariant(product, [chip.dataset.mbChipValue]);
    card.dataset.variantId = variant.id;

    var containerEl = card.closest('[data-mb-context]');
    var bundleId = containerEl ? containerEl.dataset.mbContext : null;
    // Persists the pick so it survives the full card re-render that
    // fires on every bundle:changed (renderCard has no other memory of
    // what was clicked) - see setSelectedOption/getSelectedOption in
    // product.js. Also what makes the size actually show once selected
    // instead of reverting to "no size chosen" on the very next render.
    productUI.setSelectedOption(bundleId, product.id, chip.dataset.mbChipValue);

    // A size is now explicitly chosen, so Select/Add no longer needs to
    // wait for one - enable it immediately rather than only on the next
    // full re-render (harmless no-op if it was already enabled).
    var toggle = utils.qs('.mb-product-card__select-toggle', card);
    if (toggle) toggle.disabled = false;

    var priceRow = utils.qs('.mb-product-card__price-row', card);
    if (priceRow) {
      var bundleCtx = bundleId ? store.getContext(bundleId) : null;
      var pct = bundleCtx ? discount.getTier(bundleCtx.rules, discount.countItems(bundleCtx.items) + 1) : null;
      var pctVal = pct ? pct.discount_percent : 0;
      var effective = pctVal ? Math.round(variant.price * (1 - pctVal / 100)) : variant.price;
      priceRow.innerHTML =
        '<span class="mb-price">' + utils.formatMoney(effective) + '</span>' +
        (variant.compare_at_price > variant.price
          ? '<span class="mb-price mb-price--compare">' + utils.formatMoney(variant.compare_at_price) + '</span>'
          : '');
    }
  }

  /**
   * FLIP-style width animation for .mb-product-card__select-row's
   * fit-content <-> 100% toggle (see multibuy.css). A plain CSS
   * transition can't animate this on its own: the normal (non-replace)
   * toggle below fires a full grid re-render, so by the time the row's
   * new class is in place it's already on a brand-new DOM node with no
   * prior state to transition from - it would just snap. Callers
   * capture the row's width right before whatever change is coming,
   * then hand the (possibly brand new) row + that captured width in
   * here once the change has landed, so the width can be pinned back to
   * its old value and animated forward to the new one.
   */
  function flipWidth(el, beforeWidth) {
    if (!el || beforeWidth == null) return;
    var afterWidth = el.getBoundingClientRect().width;
    if (Math.abs(afterWidth - beforeWidth) < 1) return;
    el.style.transition = 'none';
    el.style.width = beforeWidth + 'px';
    el.offsetWidth; // force reflow before flipping to the animated end state
    requestAnimationFrame(function () {
      el.style.transition = 'width 0.3s ease';
      el.style.width = afterWidth + 'px';
    });
    el.addEventListener('transitionend', function cleanup() {
      el.style.transition = '';
      el.style.width = '';
      el.removeEventListener('transitionend', cleanup);
    });
  }

  /**
   * Single handler shared by every grid (Create Your Own Pack and the
   * bottom sheet):
   *   - Replace mode active (ctx.replaceIndex set): the grid is already
   *     filtered to hide selected products, so any card clicked here is
   *     guaranteed new - replace the remembered slot and exit replace mode.
   *   - Otherwise: toggle - remove if already in the bundle, add if not.
   * This is the one and only add/remove/replace code path - the card's
   * "Added" state (and every other card's live discount preview) is
   * redrawn by the grid controller's bundle:changed subscription, so no
   * direct DOM mutation is needed here.
   */
  function handleCardAction(card, bundleId) {
    var product = card._mbProduct;
    var ctx = store.getContext(bundleId);
    if (!product || !ctx) return;

    var isReplacing = ctx.replaceIndex !== null && ctx.replaceIndex !== undefined;

    // Replace Selection: nothing is written to the real pack (ctx.items)
    // until the shopper actually leaves this page via the back arrow or
    // "View Multi Buy Pack" - see commitPendingReplacement, wired to
    // both in bindSheetEvents. Clicking cards here only stages/un-stages
    // a pending pick (sheet.pendingReplacement) and patches that one
    // card's checked look directly - simple, and it means freely
    // checking/unchecking never touches ctx.items or ctx.replaceIndex
    // (an array index a real removal would otherwise shift/corrupt).
    if (isReplacing) {
      var pending = sheet.pendingReplacement;
      var alreadyPending = pending && pending.toItemValue.productId === product.id;

      if (alreadyPending) {
        setCardPendingState(product.id, false);
        sheet.pendingReplacement = null;
        renderReplaceFooter(ctx);
        return;
      }

      if (requiresSizeChoice(product) && productUI.getSelectedOption(bundleId, product.id) == null) {
        utils.dispatch('multibuy:toast', { message: 'Please select a size first', kind: 'size' });
        return;
      }

      var replaceVariantId = card.dataset.variantId;
      var replaceVariant = product.variants.filter(function (v) { return String(v.id) === String(replaceVariantId); })[0];
      if (!replaceVariant || !replaceVariant.available) return;

      // Whichever pack the shopper is actively touching should be the
      // one the shared sticky footer reflects - deferred until every
      // guard above has passed (rather than at the top of this
      // function) so switching "active" never fires on its own, ahead
      // of any real change - see the normal-toggle branch below for why
      // that ordering matters.
      stageReplacement(bundleId, toItem(product, replaceVariant, ctx.items[ctx.replaceIndex].quantity));
      return;
    }

    // Normal (non-replace) toggle: add/remove immediately, as always.
    // Captured before the mutation below, since the grid controller's
    // bundle:changed subscription re-renders this card's whole subtree
    // the moment store.addItem/removeItem fires - see flipWidth.
    var containerEl = card.closest('[data-mb-context]');
    var row = utils.qs('.mb-product-card__select-row', card);
    var beforeWidth = row ? row.getBoundingClientRect().width : null;

    if (store.isSelected(bundleId, product.id)) {
      var existing = ctx.items.filter(function (i) { return i.productId === product.id; })[0];
      if (!existing) return;
      store.setActive(bundleId);
      store.removeItem(bundleId, existing.key);
    } else {
      // Not disabled outright when unpicked (see product.js's
      // renderCard) - tell the shopper what's missing instead of just
      // leaving the button inert.
      if (requiresSizeChoice(product) && productUI.getSelectedOption(bundleId, product.id) == null) {
        utils.dispatch('multibuy:toast', { message: 'Please select a size first', kind: 'size' });
        return;
      }
      var variantId = card.dataset.variantId;
      var variant = product.variants.filter(function (v) { return String(v.id) === String(variantId); })[0];
      if (!variant || !variant.available) return;
      // Whichever pack the shopper is actively touching should be the
      // one the shared sticky footer reflects - without this, building
      // a curated pack and then adding to Create Your Own Pack (or vice
      // versa) would silently keep showing whichever pack happened to
      // be active last, making the other look like it "disappeared"
      // even though its items are untouched. Deferred until here
      // (rather than the top of this function, past every guard above)
      // because switching "active" fires a render() for the newly
      // active bundle immediately - if that bundle still had 0 items at
      // that instant (about to gain its first one, or blocked by the
      // toast above), the shared footer would hide itself with nothing
      // left to bring it back, since no store mutation ever followed to
      // re-trigger a render.
      store.setActive(bundleId);
      store.addItem(bundleId, toItem(product, variant, 1));
    }

    if (containerEl && beforeWidth != null) {
      var newRow = utils.qs('.mb-product-card[data-product-id="' + String(product.id) + '"] .mb-product-card__select-row', containerEl);
      flipWidth(newRow, beforeWidth);
    }
  }

  /** Directly patches one Replace Selection card's checked look (root
   * "added" class + fab checkmark) without touching ctx.items - see
   * handleCardAction's isReplacing branch above. Mirrors exactly what
   * product.js's renderCard sets for an "added" card in mode: 'add'. */
  function setCardPendingState(productId, checked) {
    var el = getSheetEl();
    if (!el) return;
    var card = utils.qs('.mb-product-card[data-product-id="' + productId + '"]', el);
    if (!card) return;
    var row = utils.qs('.mb-product-card__select-row', card);
    var beforeWidth = row ? row.getBoundingClientRect().width : null;
    card.classList.toggle('mb-product-card--added', checked);
    // Mirrors exactly what product.js's renderCard puts in the
    // select-row for an "added" card: the checkmark glyph + label text.
    var toggle = utils.qs('.mb-product-card__select-toggle', card);
    if (toggle) {
      toggle.setAttribute('aria-checked', checked ? 'true' : 'false');
      var checkbox = utils.qs('.mb-product-card__checkbox', toggle);
      if (checkbox) {
        checkbox.innerHTML = checked
          ? '<svg width="10" height="10" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : '';
      }
      var label = toggle.querySelector('span:last-child');
      if (label) label.textContent = checked ? 'Selected' : 'Select';
    }
    flipWidth(row, beforeWidth);
  }

  // Both the grid and the product drawer stage the same deferred swap.
  function stageReplacement(bundleId, item) {
    var ctx = store.getContext(bundleId);
    if (!ctx || sheet.bundleId !== bundleId || sheet.contentMode !== 'replace' ||
        ctx.replaceIndex == null || !ctx.items[ctx.replaceIndex]) return false;
    var pending = sheet.pendingReplacement;
    store.setActive(bundleId);
    if (pending) setCardPendingState(pending.toItemValue.productId, false);
    sheet.pendingReplacement = { fromItem: ctx.items[ctx.replaceIndex], toItemValue: item };
    setCardPendingState(item.productId, true);
    renderReplaceFooter(ctx);
    return true;
  }

  function getPendingReplacement(bundleId) {
    return sheet.bundleId === bundleId && sheet.pendingReplacement
      ? sheet.pendingReplacement.toItemValue : null;
  }

  function completeReplacement(bundleId) {
    if (sheet.bundleId !== bundleId) return;
    commitPendingReplacement(bundleId);
    endReplaceMode(bundleId);
  }

  /** Actually performs the deferred swap - called only when the shopper
   * leaves the Replace Selection page (back arrow / "View Multi Buy
   * Pack"), never from a card click. No-ops if nothing was picked. */
  function commitPendingReplacement(bundleId) {
    var ctx = store.getContext(bundleId);
    var pending = sheet.pendingReplacement;
    if (!ctx || !pending) return;
    var replaceIdx = ctx.replaceIndex;
    if (replaceIdx === null || replaceIdx === undefined) return;
    var fromItem = pending.fromItem;
    store.replaceItem(bundleId, replaceIdx, pending.toItemValue);
    // Swap the slot in the pack's fixed membership too (by product id,
    // not array index, since deselect/reselect can reorder ctx.items) -
    // this is a genuine slot swap, unlike a plain deselect, so the old
    // product should stop showing in the default view and the new one
    // should start.
    if (ctx.packProductIds && fromItem) {
      var packIdx = ctx.packProductIds.indexOf(fromItem.productId);
      if (packIdx !== -1) ctx.packProductIds[packIdx] = pending.toItemValue.productId;
      else ctx.packProductIds.push(pending.toItemValue.productId);
    }
    sheet.pendingReplacement = null;
  }

  // Tag each rendered card with a reference to its product object for
  // fast lookup on click (avoids re-parsing dataset JSON per click).
  var originalRenderGrid = productUI.renderGrid;
  productUI.renderGrid = function (products, ctx, mode) {
    var html = originalRenderGrid(products, ctx, mode);
    // Defer attaching product refs until the HTML lands in the DOM.
    requestAnimationFrame(function () {
      utils.qsa('.mb-product-card').forEach(function (card) {
        if (card._mbProduct) return;
        var match = products.filter(function (p) { return String(p.id) === card.dataset.productId; })[0];
        if (match) card._mbProduct = match;
      });
    });
    return html;
  };

  // ---------------------------------------------------------------------
  // View Collection (full-page drawer) + My Cart popup (bottom sheet)
  //
  // These used to be the exact same DOM element/class, which meant a
  // change aimed at one (e.g. converting View Collection to a full-page
  // drawer) silently changed the other too. They're now two entirely
  // separate elements/classes ([data-mb-collection-page] vs
  // [data-mb-sheet]) so each can evolve independently, while still
  // sharing this one "sheet" state object + open/close/custom-content
  // helpers so neither mechanic is duplicated.
  // ---------------------------------------------------------------------

  var sheet = {
    kind: null, // 'collection' | 'cart' - which element is currently open
    collectionEl: null,
    cartEl: null,
    bundleId: null,
    gridController: null,
    contentMode: null, // 'browse' | 'replace' | 'custom'
    buildHtml: null,
    // { fromItem, toItemValue } - staged, not yet written to ctx.items -
    // while picking a replacement on the Replace Selection page. Only
    // becomes real via commitPendingReplacement, when the shopper leaves
    // the page. Cleared on entering/leaving replace mode.
    pendingReplacement: null
  };

  function getCollectionEl() {
    if (!sheet.collectionEl) sheet.collectionEl = utils.qs('[data-mb-collection-page]');
    return sheet.collectionEl;
  }

  function getCartEl() {
    if (!sheet.cartEl) sheet.cartEl = utils.qs('[data-mb-sheet]');
    return sheet.cartEl;
  }

  function getCartBackdropEl() {
    return utils.qs('[data-mb-sheet-backdrop]');
  }

  // Kept for existing call sites in this file that still just mean
  // "whichever shell is currently open" (renderSheet, replace banner, etc.)
  function getSheetEl() {
    return sheet.kind === 'cart' ? getCartEl() : getCollectionEl();
  }

  function ensureOpen(bundleId, kind) {
    store.setActive(bundleId);
    sheet.bundleId = bundleId;
    sheet.kind = kind;

    var el = kind === 'cart' ? getCartEl() : getCollectionEl();
    if (!el) return null;
    el.hidden = false;
    utils.lockScroll(true);
    if (kind === 'cart') {
      var backdrop = getCartBackdropEl();
      if (backdrop) backdrop.hidden = false;
      requestAnimationFrame(function () {
        if (backdrop) backdrop.classList.add('mb-sheet-backdrop--visible');
      });
    }
    requestAnimationFrame(function () {
      el.classList.add(kind === 'cart' ? 'mb-sheet--open' : 'mb-collection-page--open');
    });
    return el;
  }

  // Desktop only: View Collection's overlay sits below the theme's real
  // <header> instead of covering it (see the min-width:769px rule on
  // .mb-collection-page in multibuy.css, which reads this custom
  // property). Measured at runtime rather than hardcoded, since header
  // height varies by theme customizer settings (announcement bar, logo
  // size, sticky vs static) and isn't something this app controls.
  // Mobile never reads this variable, so it's a no-op there.
  var headerOffsetResizeBound = false;
  function applyDesktopHeaderOffset() {
    if (window.innerWidth < 769) return;
    var header = document.querySelector('header');
    var height = header ? header.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--mb-header-h', height + 'px');
    if (!headerOffsetResizeBound) {
      headerOffsetResizeBound = true;
      window.addEventListener('resize', utils.debounce(applyDesktopHeaderOffset, 150));
    }
  }

  function open(bundleId) {
    var ctx = store.getContext(bundleId);
    if (!ctx) return;
    // Desktop only (mobile's overlay is always top: 0, so this can't
    // happen there): the header-offset math only lines up if the real
    // <header> is actually sitting at the top of the viewport at the
    // moment its height gets measured. If the shopper had scrolled down
    // first and this theme's header isn't the kind that stays pinned
    // while scrolling, it would already be scrolled out of view, so the
    // measured height reserves a gap above the overlay that has nothing
    // real behind it - whatever page content happened to be at that
    // scroll position (e.g. a curated card) shows through instead.
    // Snapping to the top first guarantees the header is where it's
    // expected to be before it's measured.
    // Forces the jump to be instant regardless of any theme-level
    // `html { scroll-behavior: smooth }` (very common, e.g. for anchor
    // links) - the 'instant' option alone isn't reliably honored on
    // every browser once CSS scroll-behavior is set, so scroll-behavior
    // is also force-reset to 'auto' inline right before scrolling (and
    // restored after) as a second guarantee. Without this, the scroll
    // would animate over a few hundred ms instead of jumping
    // immediately, and the header height measured right after would be
    // read mid-scroll (header not actually at the top yet), reproducing
    // the exact same glitch this was meant to fix.
    if (window.innerWidth >= 769) {
      var htmlEl = document.documentElement;
      var prevScrollBehavior = htmlEl.style.scrollBehavior;
      htmlEl.style.scrollBehavior = 'auto';
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      htmlEl.style.scrollBehavior = prevScrollBehavior;
    }
    applyDesktopHeaderOffset();
    // Belt-and-suspenders re-measurement one frame later: if the theme's
    // own header has any size-changing transition of its own (e.g. an
    // announcement bar collapsing, a shrink-on-scroll effect settling
    // back to full size), that can still be mid-flight in the same tick
    // as the scroll reset above. Re-reading the height after a paint
    // catches that instead of locking in a transient value.
    if (window.innerWidth >= 769) requestAnimationFrame(applyDesktopHeaderOffset);
    var el = ensureOpen(bundleId, 'collection');
    if (!el) return;
    sheet.contentMode = 'browse';

    // Paints a spinner into the panel the instant it opens, before the
    // auto-fill fetch (first open) or the deferred renderSheet below has
    // had a chance to run - without this, whatever was in the panel from
    // last time (or nothing at all) just sat there dimmed while empty,
    // which read as the page hanging/lagging rather than loading.
    // renderSheet's own el.innerHTML replaces this outright once ready,
    // so there's nothing to explicitly clear.
    el.innerHTML = loaderHtml();
    ensureAutoFilled(ctx).then(function () {
      // Deferred one frame: ensureAutoFilled's promise resolves as a
      // microtask, which (when the pack is already filled, the common
      // case on repeat opens) can run before ensureOpen's own
      // requestAnimationFrame above has even fired - stacking
      // renderSheet's heavy synchronous DOM build onto the exact same
      // frame the slide-in transition is trying to start on, which is
      // what was making the open animation stutter on desktop. Waiting
      // for the next frame here lets that transition get a clean first
      // paint before the heavy work competes with it on the main thread.
      requestAnimationFrame(function () {
        renderSheet(bundleId);
      });
    });
  }

  // Reuses the same shimmer skeleton language as the grid's own loading
  // state (skeletonGridHtml, used by createGridController.load()) so the
  // page doesn't jump from a spinner to a shimmer once renderSheet takes
  // over a moment later - it's shimmer the whole way through.
  function loaderHtml() {
    return (
      '<div class="mb-collection-page__loader">' +
      '<div class="mb-skeleton-line mb-skeleton-line--heading"></div>' +
      '<div class="mb-skeleton-line mb-skeleton-line--price"></div>' +
      '<div class="mb-product-grid">' + skeletonGridHtml(8) + '</div>' +
      '</div>'
    );
  }

  /**
   * Reuses the open/close/scroll-lock mechanics (not the DOM element -
   * see above) for arbitrary custom content instead of the
   * product-browse grid - e.g. bundle.js's "My Cart" summary popup,
   * opened from the footer's "View Details". buildHtml is re-invoked by
   * refresh() so callers can keep the content live without
   * re-implementing the open/close mechanics.
   */
  function openWithContent(bundleId, buildHtml) {
    var ctx = store.getContext(bundleId);
    if (!ctx) return;
    var el = ensureOpen(bundleId, 'cart');
    if (!el) return;
    if (sheet.gridController) {
      sheet.gridController.destroy();
      sheet.gridController = null;
    }
    sheet.contentMode = 'custom';
    sheet.buildHtml = buildHtml;
    el.innerHTML = buildHtml();
  }

  // bundleId is accepted (most callers pass whichever bundle just
  // changed) but not required to match sheet.bundleId - the My Cart
  // popup's content now aggregates every bundle with items, not just
  // the one that happened to open it, so a change to ANY bundle while
  // it's open should refresh it.
  function refreshCustomContent() {
    var el = getCartEl();
    if (!el || el.hidden || sheet.contentMode !== 'custom' || !sheet.buildHtml) return;
    el.innerHTML = sheet.buildHtml();
  }

  // No bundleId param, matching refreshCustomContent above - the My Cart
  // popup aggregates every bundle with items, so "is it open" doesn't
  // depend on which one originally triggered it.
  function isShowingCustomContent() {
    var el = getCartEl();
    return !!el && !el.hidden && sheet.contentMode === 'custom';
  }

  function ensureAutoFilled(ctx) {
    if (ctx.items.length || ctx.type !== 'curated') return Promise.resolve();
    return api.getCollectionProducts(ctx.collectionHandle).then(function (products) {
      // Preserve available curated picks; replace only unavailable slots.
      var originals = products.slice(0, ctx.minSlots);
      var available = MB.filter.stockOrderedProducts(products);
      var originalIds = originals.map(function (p) { return p.id; });
      var replacements = available.filter(function (p) { return originalIds.indexOf(p.id) === -1; });
      var slots = originals.map(function (p) {
        return p.variants.some(function (v) { return v.available; }) ? p : replacements.shift();
      }).filter(Boolean);
      // A short collection must never create phantom or duplicate items.
      while (slots.length < ctx.minSlots && replacements.length) slots.push(replacements.shift());
      slots.forEach(function (p) {
        var variant = productUI.firstAvailableOptionValues(p);
        var v = productUI.pickVariant(p, variant);
        store.addItem(ctx.id, toItem(p, v, 1));
        // So the pack view's chip shows the auto-picked size as
        // selected too, instead of looking like no size was ever
        // chosen for a slot that's already in the bundle.
        if (variant[0]) productUI.setSelectedOption(ctx.id, p.id, variant[0]);
      });
      // Fixed pack membership for the sheet's default view - see
      // applyFiltersAndSort/onlySelected. Deselecting a card later must
      // not shrink this list; only a completed Replace Selection does.
      ctx.packProductIds = slots.map(function (p) { return p.id; });
    });
  }

  function close() {
    var kind = sheet.kind;
    var el = kind === 'cart' ? getCartEl() : getCollectionEl();
    if (!el) return;
    el.classList.remove(kind === 'cart' ? 'mb-sheet--open' : 'mb-collection-page--open');
    if (kind === 'cart') {
      var backdrop = getCartBackdropEl();
      if (backdrop) backdrop.classList.remove('mb-sheet-backdrop--visible');
    }
    utils.lockScroll(false);
    if (sheet.bundleId && store.getContext(sheet.bundleId)) {
      store.setReplaceIndex(sheet.bundleId, null);
    }
    if (sheet.gridController) {
      sheet.gridController.destroy();
      sheet.gridController = null;
    }
    sheet.bundleId = null;
    sheet.kind = null;
    sheet.contentMode = null;
    sheet.buildHtml = null;
    // Discard rather than commit - closing the whole page is a
    // different action from explicitly clicking back/"View Multi Buy
    // Pack" (the only two places that actually write a pending pick).
    sheet.pendingReplacement = null;
    // Matches (with a small buffer) the 0.3s transform transition on
    // .mb-collection-page/.mb-sheet - at 250ms this fired 50ms before
    // the slide-out actually finished, cutting the animation off early
    // (display: none snapping in mid-slide) instead of letting it
    // complete, which read as a jerky/not-smooth close.
    setTimeout(function () {
      el.hidden = true;
      if (kind === 'cart') {
        var backdrop = getCartBackdropEl();
        if (backdrop) backdrop.hidden = true;
      }
    }, 320);
  }

  /**
   * Renders the sheet shell once: title + live pricing header, the
   * "Replace Selection" banner (hidden unless replace mode is active),
   * the search/sort toolbar, and the SAME product grid component used
   * by Create Your Own Pack (mode: 'select', nothing excluded normally -
   * products already in the bundle render with their fab in the
   * "added" state via MB.product.renderCard, and clicking that fab
   * again removes them; each added card also gets a "Replace Selection"
   * link that hides selected products and lets the shopper swap that
   * slot for something else - see handleReplaceTrigger/handleCardAction).
   */
  /**
   * Desktop-only "Home > Curated multi-buy packs > {Bundle}" breadcrumb
   * for the View Collection page's header - hidden on mobile via CSS, so
   * that layout is untouched. Mirrors the same breadcrumb pattern already
   * used on the cart page (main-cart.liquid): links to the storefront
   * root and to pages/multi-buy. The middle label is read straight off
   * the landing page's own heading (.mb-header__heading, e.g. "Curated
   * multi-buy packs") - same DOM tree, since View Collection opens as an
   * overlay on top of that page rather than a real navigation - falling
   * back to "Multi Buy" if that heading isn't found for any reason.
   */
  function renderCollectionBreadcrumb(ctx, currentLabel, midAction) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    root = root.replace(/\/$/, '');
    var headingEl = utils.qs('.mb-header__heading');
    var midLabel = headingEl && headingEl.textContent.trim() ? headingEl.textContent.trim() : 'Multi Buy';
    var sepSvg = '<span class="mb-breadcrumb__sep" aria-hidden="true">&rsaquo;</span>';
    return (
      '<nav class="mb-breadcrumb" aria-label="Breadcrumb">' +
      '<a class="mb-breadcrumb__link" href="' + root + '/">Home</a>' +
      sepSvg +
      // View Collection's breadcrumb closes the whole overlay back to the
      // landing page (sheet-close, the default). Replace Selection passes
      // 'cancel-replace' instead, so clicking this same crumb there
      // returns to the bundle's own pack view (this bundle's "curated
      // card collection page"), not all the way out to the landing page.
      '<a class="mb-breadcrumb__link" type="button" data-mb-action="' + (midAction || 'sheet-close') + '" aria-label="Back" >' + utils.escapeHtml(midLabel) + '</a>' +
      sepSvg +
      '<span class="mb-breadcrumb__current" aria-current="page">' + utils.escapeHtml(currentLabel || ctx.title) + '</span>' +
      '</nav>'
    );
  }

  function renderSheet(bundleId) {
    var el = getSheetEl();
    var ctx = store.getContext(bundleId);
    if (!el || !ctx) return;

    if (sheet.gridController) {
      sheet.gridController.destroy();
      sheet.gridController = null;
    }

    el.innerHTML =
    renderCollectionBreadcrumb(ctx) +
      '<div class="collection-header"> <div class="mb-sheet__header">' +
      '<button type="button" class="collection-back" data-mb-action="sheet-close" aria-label="Back"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Free 7.3.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2026 Fonticons, Inc.--><path d="M169.4 297.4C156.9 309.9 156.9 330.2 169.4 342.7L361.4 534.7C373.9 547.2 394.2 547.2 406.7 534.7C419.2 522.2 419.2 501.9 406.7 489.4L237.3 320L406.6 150.6C419.1 138.1 419.1 117.8 406.6 105.3C394.1 92.8 373.8 92.8 361.3 105.3L169.3 297.3z"/></svg></button>' +
      '<div><h3 class="mb-sheet__title">' + utils.escapeHtml(ctx.title) + '</h3>' +
      '<div class="mb-sheet__pricing" data-mb-sheet-pricing="' + ctx.id + '"></div>' +
      '</div>' +
      '</div>' +
      '<div class="mb-sheet__next-tier" data-mb-next-tier="' + ctx.id + '"></div>' +
      '</div>' +
      // Below the title/price + next-tier row (see the reference layout),
      // not above the title - full width of its own, not squeezed into
      // .collection-header's flex row with the two columns above.
      '<div class="mb-sheet__replace-banner" data-mb-replace-banner="' + ctx.id + '" hidden></div>' +
      // '<div class="mb-sheet__toolbar" data-mb-toolbar></div>' +
      '<div class="mb-sheet__body"><div class="mb-product-grid" data-mb-grid></div>' +
      // Desktop-only inline copy of the same footer content (stats +
      // CTA) - shares the data-mb-sheet-summary attribute with the
      // pinned one below, so the existing
      // qsa('[data-mb-sheet-summary="id"]') refresh logic in
      // bundle.js's render() keeps both in sync automatically, with no
      // extra wiring. On desktop the pinned one is hidden and this one
      // shows instead, sticky to the bottom of the viewport by default
      // (see .mb-sheet__footer--inline in multibuy.css). Mobile never
      // shows this (display:none), so it keeps using the pinned footer
      // exactly as before.
      '<div class="mb-sheet__footer mb-sheet__footer--inline" data-mb-sheet-summary="' + ctx.id + '"></div>' +
      '<div class="mb-sheet__explore" data-mb-sheet-explore="' + ctx.id + '"></div>' +
      '</div>' +
      '<div class="mb-sheet__footer" data-mb-sheet-summary="' + ctx.id + '"></div>';

    bindToolbar(
      utils.qs('[data-mb-toolbar]', el),
      function (query) {
        sheet.gridController.setQuery(query);
      },
      function (filters) {
        sheet.gridController.setFilters(filters);
      },
      function (sortKey) {
        sheet.gridController.setSort(sortKey);
      },
      function () {
        return sheet.gridController ? sheet.gridController.getProducts() : [];
      },
      function (product) {
        productUI.openQuickView(product.handle, bundleId);
      }
    );

    sheet.gridController = createGridController(utils.qs('[data-mb-grid]', el), {
      bundleId: bundleId,
      collectionHandle: ctx.collectionHandle,
      globalCatalog: true,
      mode: 'select',
      excludeSelected: false,
      // Default view: just the pack's own products, not the whole
      // collection - Replace Selection (startReplace) switches this off
      // and excludeSelected on to browse the full catalog instead.
      onlySelected: true
    });
    sheet.gridController.load();

    renderSheetPricing(ctx);
    renderNextTierBanner(ctx);
    utils.qsa('[data-mb-sheet-summary="' + ctx.id + '"]').forEach(function (sumEl) {
      sumEl.innerHTML = MB.bundleUI.renderSheetSummary(ctx);
    });
    // renderExplorePacks(bundleId);
  }

  /**
   * Desktop-only "Explore other Curated multi-buy packs" - hidden on
   * mobile via CSS. Lives inside .mb-sheet__body (the same scrollable
   * region as the product grid) so it's revealed by scrolling down past
   * the grid/CTA, not a separate page. Rather than re-rendering bundle
   * cards from data (the store only holds selection state, not a
   * bundle's marketing price/cover image/etc.), this deep-clones the
   * already-rendered curated cards from the landing page's own slider
   * (skipping the bundle currently being viewed) - guarantees an exact
   * visual match with zero duplicate markup, and "View Collection"/
   * "Share" on the clones work immediately since those actions are
   * handled by document-level click delegation (see multibuy.js's
   * bindCuratedCardActions), not per-node listeners.
   */
  function renderExplorePacks(bundleId) {
    var el = utils.qs('[data-mb-sheet-explore="' + bundleId + '"]');
    if (!el) return;

    var otherCards = utils.qsa('[data-mb-curated-card]:not([data-bundle-id="' + bundleId + '"])');
    var slides = otherCards.map(function (card) {
      return card.closest('.mb-slider__slide') || card;
    });

    if (!slides.length) {
      el.innerHTML = '';
      return;
    }

    el.innerHTML =
      '<h3 class="mb-sheet-explore__heading">Explore other Curated multi-buy packs</h3>' +
      '<div class="mb-slider mb-sheet-explore__slider"><div class="mb-slider__track" data-mb-slider-track></div></div>';

    var track = utils.qs('[data-mb-slider-track]', el);
    slides.forEach(function (slide) {
      var clone = slide.cloneNode(true);
      var script = utils.qs('script', clone);
      if (script) script.remove();
      track.appendChild(clone);
    });
  }

  function renderSheetPricing(ctx) {
    var el = utils.qs('[data-mb-sheet-pricing="' + ctx.id + '"]');
    if (!el) return;
    var totals = discount.computeTotals(ctx.items, ctx.rules);
    el.innerHTML =
      '<strong>' + utils.formatMoney(totals.total) + '</strong> ' +
      (totals.compareSubtotal > totals.total ? '<span class="mb-price mb-price--compare">' + utils.formatMoney(totals.compareSubtotal) + '</span>' : '') +
      (totals.discountPercent ? ' <span class="mb-pct-save">' + totals.discountPercent + '% save</span>' : '');
  }

  /**
   * "Add N items to the cart and unlock X% OFF [save ₹Y]" - entirely
   * derived from the merchant's own discount_rule blocks (the highest
   * configured tier) and the live totals, no hardcoded thresholds.
   */
  function renderNextTierBanner(ctx) {
    var el = utils.qs('[data-mb-next-tier="' + ctx.id + '"]');
    if (!el) return;
    var rules = discount.normalizeRules(ctx.rules);
    if (!rules.length) {
      el.innerHTML = '';
      return;
    }
    var totals = discount.computeTotals(ctx.items, ctx.rules);
    var maxTier = rules[rules.length - 1];
    var remaining = Math.max(0, maxTier.products_required - totals.itemCount);

    var message;
    if (remaining <= 0) {
      message =
        'Add ' + maxTier.products_required + ' item' + (maxTier.products_required === 1 ? '' : 's') +
        ' to the cart and unlock <strong>' + maxTier.discount_percent + '% OFF</strong>';
    } else {
      message =
        'Add ' + remaining + ' item' + (remaining === 1 ? '' : 's') +
        ' to the cart and unlock <strong>' + maxTier.discount_percent + '% OFF</strong>' +
        (totals.savings > 0 ? ' save ' + utils.formatMoney(totals.savings) : '');
    }

    el.innerHTML =
      '<span class="mb-sheet__next-tier-icon" aria-hidden="true">' +
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.16141 8.57395V16.1987C2.16141 16.7927 2.64741 17.2787 3.24141 17.2787H14.7614C15.3554 17.2787 15.8414 16.7927 15.8414 16.1987V8.57395C16.259 8.42635 16.5614 8.03035 16.5614 7.55875V6.11875C16.5614 5.52475 16.0754 5.03875 15.4814 5.03875H13.0694C13.2818 4.91995 13.4798 4.78315 13.6562 4.62115C14.153 4.15675 14.405 3.57355 14.405 2.87875C14.405 2.18395 13.8758 0.71875 12.245 0.71875C10.6142 0.71875 9.54141 2.95435 9.00501 4.31515C8.46861 2.95435 7.34901 0.71875 5.76501 0.71875C4.18101 0.71875 3.60501 2.17315 3.60501 2.87875C3.60501 3.58435 3.85701 4.15675 4.35381 4.62115C4.52661 4.78315 4.72821 4.91995 4.94061 5.03875H2.52141C1.92741 5.03875 1.44141 5.52475 1.44141 6.11875V7.55875C1.44141 8.02675 1.74381 8.42275 2.16141 8.57395ZM2.88141 16.1987V8.63875H7.20141V16.5587H3.24141C3.04341 16.5587 2.88141 16.3967 2.88141 16.1987ZM7.56141 5.75875H7.88901C8.10501 5.76955 8.29941 5.77315 8.46501 5.77315C8.74941 5.77315 8.93661 5.76235 9.00141 5.75875C9.06621 5.76235 9.25341 5.77315 9.53781 5.77315C9.70341 5.77315 9.90141 5.76955 10.1138 5.75875H10.4414V7.91875H7.56141V5.75875ZM7.92141 16.5587V8.63875H10.0814V16.5587H7.92141ZM15.1214 16.1987C15.1214 16.3967 14.9594 16.5587 14.7614 16.5587H10.8014V8.63875H15.1214V16.1987ZM15.8414 6.11875V7.55875C15.8414 7.75675 15.6794 7.91875 15.4814 7.91875H11.1614V5.75875H15.4814C15.6794 5.75875 15.8414 5.92075 15.8414 6.11875ZM12.2414 1.43875C13.595 1.43875 13.6814 2.86435 13.6814 2.87875C13.6814 3.36835 13.5122 3.76795 13.163 4.09195C12.407 4.79395 11.021 4.99195 10.0634 5.03875H9.50181C9.96981 3.72475 11.0354 1.43875 12.245 1.43875H12.2414ZM4.32141 2.87875C4.32141 2.87875 4.40781 1.43875 5.76141 1.43875C6.97101 1.43875 8.03661 3.72475 8.50461 5.03875H7.92861C6.97101 4.99195 5.59581 4.79395 4.84341 4.09195C4.49421 3.76795 4.32501 3.36835 4.32501 2.87875H4.32141ZM2.16141 6.11875C2.16141 5.92075 2.32341 5.75875 2.52141 5.75875H6.84141V7.91875H2.52141C2.32341 7.91875 2.16141 7.75675 2.16141 7.55875V6.11875Z" fill="#496FEF"/></svg>' +
      '</span>' +
      '<span class="mb-sheet__next-tier-text">' + message + '</span>';
  }

  // -----------------------------------------------------------------------
  // Replace Selection - its own page ("Select Product"), not just a filter
  // toggle on the pack view: own header (no bundle title/pricing), its
  // own search/filter/sort toolbar, the full collection minus what's
  // already picked, and no CTA at the bottom until a swap is made - then
  // a "Replaced X -> Y" confirmation + a CTA the shopper uses to navigate
  // back to the pack themselves (no auto-return). Swapping itself still
  // goes through store.replaceItem via the shared handleCardAction - see
  // above.
  // -----------------------------------------------------------------------

  function startReplace(bundleId, index) {
    var ctx = store.getContext(bundleId);
    if (!ctx) return;
    store.setReplaceIndex(bundleId, index);
    sheet.pendingReplacement = null;
    renderReplaceSheet(bundleId);
  }

  /** Used by the page's back arrow and (once a pick is made) its CTA to
   * return to the normal pack view. The actual swap (if anything was
   * picked) is committed by the cancel-replace click handler in
   * bindSheetEvents, right before this runs - never here directly, so
   * this stays safe to call from anywhere replace mode needs to end. */
  function endReplaceMode(bundleId) {
    var ctx = store.getContext(bundleId);
    if (!ctx) return;
    store.setReplaceIndex(bundleId, null);
    sheet.pendingReplacement = null;
    renderSheet(bundleId);
  }

  function renderReplaceSheet(bundleId) {
    var el = getSheetEl();
    var ctx = store.getContext(bundleId);
    if (!el || !ctx) return;

    if (sheet.gridController) {
      sheet.gridController.destroy();
      sheet.gridController = null;
    }
    sheet.contentMode = 'replace';

    el.innerHTML =
      // Common wrapper so the header (back/breadcrumb/title) and the
      // toolbar (search/filter/sort) sit side by side in one row on
      // desktop, pinned to the top of the panel while the grid below
      // scrolls underneath - see .mb-replace-header in multibuy.css
      // (desktop only; mobile keeps the existing stacked layout).
      '<div class="mb-replace-header">' +
      '<div class="mb-sheet__header">' +
      '<button type="button" class="collection-back" data-mb-action="cancel-replace" aria-label="Back"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M169.4 297.4C156.9 309.9 156.9 330.2 169.4 342.7L361.4 534.7C373.9 547.2 394.2 547.2 406.7 534.7C419.2 522.2 419.2 501.9 406.7 489.4L237.3 320L406.6 150.6C419.1 138.1 419.1 117.8 406.6 105.3C394.1 92.8 373.8 92.8 361.3 105.3L169.3 297.3z"/></svg></button>' +
      // Desktop-only breadcrumb, same structure/component as the View
      // Collection page (renderCollectionBreadcrumb) - hidden on mobile
      // via the same .mb-breadcrumb CSS, so this page's mobile header is
      // unchanged. Final crumb reads "Select Product" instead of the
      // bundle title, since that's this page, not the pack view.
      '<div>' + renderCollectionBreadcrumb(ctx, 'Select Product', 'cancel-replace') + '<h3 class="mb-sheet__title">Select Product</h3></div>' +
      '</div>' +
      // Reuses search-toolbar.liquid's actual component structure, not
      // just its button styling: a .mb-toolbar wrapper with the desktop
      // "Filter" pill (dk-filter-opt) in its own left-hand div and the
      // search/sort controls in the right-hand one - exactly the same
      // two elements/classes that toolbar uses, so the exact same CSS
      // (.mb-toolbar's side-by-side desktop / stacked mobile layout,
      // dk-filter-opt/mb-filter-opt show-hide) applies here unmodified.
      // Mobile's controls row (search + the plain icon-only mb-filter-opt
      // button + sort) is byte-for-byte what this toolbar always rendered.
      '<div class="mb-toolbar">' +
      '<div class="mb-toolbar__controls mb-sheet__toolbar" data-mb-replace-toolbar>' +
      '<button type="button" class="mb-btn mb-btn--ghost mb-filter-toggle dk-filter-opt" data-mb-filter-toggle>' +
      '<svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M9.99876 3.84528V2.13672M9.99876 3.84528C9.07612 3.84528 8.32817 4.61023 8.32817 5.55384C8.32817 6.49745 9.07612 7.2624 9.99876 7.2624M9.99876 3.84528C10.9214 3.84528 11.6693 4.61023 11.6693 5.55384C11.6693 6.49745 10.9214 7.2624 9.99876 7.2624M9.99876 7.2624L9.99876 15.8052M4.98699 14.0966C5.90963 14.0966 6.65758 13.3317 6.65758 12.3881C6.65758 11.4445 5.90963 10.6795 4.98699 10.6795M4.98699 14.0966C4.06435 14.0966 3.31641 13.3317 3.31641 12.3881C3.31641 11.4445 4.06435 10.6795 4.98699 10.6795M4.98699 14.0966L4.98699 15.8052M4.98699 10.6795L4.98699 2.13672M15.0105 14.0966C15.9332 14.0966 16.6811 13.3317 16.6811 12.3881C16.6811 11.4445 15.9332 10.6795 15.0105 10.6795M15.0105 14.0966C14.0879 14.0966 13.3399 13.3317 13.3399 12.3881C13.3399 11.4445 14.0879 10.6795 15.0105 10.6795M15.0105 14.0966V15.8052M15.0105 10.6795L15.0105 2.13672" stroke="white" stroke-width="1.4219" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>' +
      'Filter' +
      '</button>' +
      '<div class="mb-search-wrap">' +
      '<input type="search" class="mb-input" data-mb-search placeholder="Search" autocomplete="off">' +
      '<button type="button" class="mb-search-clear" data-mb-search-clear hidden aria-label="Clear search">&times;</button>' +
      '<img class="mb-search-icon" data-mb-search-icon src="https://cdn.shopify.com/s/files/1/0589/9268/9284/files/search.svg?v=1784367077" alt="" aria-hidden="true">' +
      '</div>' +
      '<button type="button" class="mb-btn mb-btn--ghost mb-filter-toggle mb-filter-opt" data-mb-filter-toggle>' +
      '<img src="https://cdn.shopify.com/s/files/1/0589/9268/9284/files/filter-1.svg?v=1784367053" alt="">' +
      '</button>' +
      '<select class="mb-select" data-mb-sort aria-label="Sort products">' +
      '<option>Sort By</option>' +
      '<option value="featured">Featured</option>' +
      '<option value="newest">Newest</option>' +
      '<option value="alphabetical">Alphabetical</option>' +
      '<option value="price-asc">Price: Low to High</option>' +
      '<option value="price-desc">Price: High to Low</option>' +
      '</select>' +
      '<div class="mb-search-suggestions" data-mb-search-suggestions hidden></div>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '<aside class="mb-filter-panel" data-mb-filter-panel>' +
      '<div class="mb-filter-panel__heading">' +
      '<span>Filters</span>' +
      '<button type="button" class="mb-icon-btn" data-mb-filter-close aria-label="Close filters">&times;</button>' +
      '</div>' +
      ['size', 'pattern', 'fit', 'style', 'fabric', 'color']
        .map(function (key) {
          return (
            '<details class="mb-filter-group" data-mb-filter-group="' + key + '" hidden>' +
            '<summary class="mb-filter-group__title">' + key.charAt(0).toUpperCase() + key.slice(1) + '</summary>' +
            '<div data-mb-facet-' + key + '></div>' +
            '</details>'
          );
        })
        .join('') +
      '<details class="mb-filter-group">' +
      '<summary class="mb-filter-group__title">Price</summary>' +
      '<div class="mb-price-inputs">' +
      '<input type="number" class="mb-input" data-mb-filter-min placeholder="Min" min="0">' +
      '<input type="number" class="mb-input" data-mb-filter-max placeholder="Max" min="0">' +
      '</div>' +
      '<div class="mb-price-slider" data-mb-price-slider></div>' +
      '</details>' +
      '<div class="mb-filter-panel__actions">' +
      '<button type="button" class="mb-btn mb-btn--ghost" data-mb-filter-reset>Reset</button>' +
      '<button type="button" class="mb-btn mb-btn--primary" data-mb-filter-apply>Apply</button>' +
      '</div>' +
      '</aside>' +
      '<div class="mb-sheet__body"><div class="mb-product-grid" data-mb-grid></div></div>' +
      '<div class="mb-sheet__footer" data-mb-replace-footer="' + ctx.id + '"></div>';

    var toolbarWrap = utils.qs('[data-mb-replace-toolbar]', el);
    MB.filterUI.bindSearchInput(toolbarWrap, {
      setQuery: function (q) { sheet.gridController.setQuery(q); },
      getProducts: function () { return sheet.gridController ? sheet.gridController.getProducts() : []; }
    }, bundleId);
    MB.filterUI.bindSortSelect(toolbarWrap, { setSort: function (s) { sheet.gridController.setSort(s); } });

    // Bright, plain-checkbox cards with no "unselected" dimming and no
    // "Replace Selection" link - every product shown here starts out not
    // in the pack, so there's nothing to visually contrast against - see
    // product.js's renderCard. excludeIds is a frozen snapshot of the
    // pack taken now, not re-derived from live ctx.items on every render
    // - otherwise the moment a card is picked it'd count as "selected"
    // and the excludeSelected filter would make it disappear instead of
    // staying put and showing checked.
    //
    // Uses packProductIds (the pack's full fixed slot set), not
    // ctx.items (only the currently-checked ones) - otherwise a product
    // that's still part of the pack but just unchecked (see the
    // onlySelected/packProductIds dimming behavior on the main pack
    // view) would show up here as if it were a fresh, pickable option,
    // even though it already occupies one of the pack's slots.
    var excludeIds = ctx.packProductIds && ctx.packProductIds.length
      ? ctx.packProductIds.slice()
      : ctx.items.map(function (i) { return i.productId; });
    sheet.gridController = createGridController(utils.qs('[data-mb-grid]', el), {
      bundleId: bundleId,
      collectionHandle: ctx.collectionHandle,
      globalCatalog: true,
      mode: 'add',
      excludeSelected: true,
      excludeIds: excludeIds,
      onlySelected: false
    });
    sheet.gridController.load();

    MB.filterUI.bindFilterPanel(utils.qs('[data-mb-filter-panel]', el), sheet.gridController, el);

    renderReplaceFooter(ctx);
  }

  /** Empty until a pick is made (no CTA - nothing to confirm yet), then a
   * persistent "Replacing X -> Y" preview of the pending swap + the CTA
   * the shopper uses to commit it and navigate back - see
   * commitPendingReplacement/endReplaceMode. */
  function renderReplaceFooter(ctx) {
    var el = utils.qs('[data-mb-replace-footer="' + ctx.id + '"]');
    if (!el) return;
    var pending = sheet.pendingReplacement;
    el.hidden = !pending;
    if (!pending) {
      el.innerHTML = '';
      return;
    }
    var buttonReplace=  '<button type="button" class="mb-btn mb-btn--primary mb-btn--block view-bundle dk-visible" data-mb-action="cancel-replace">' +
      '<span class="mb-btn__label">&larr; View Multi Buy Pack</span>' +
      '</button>';
    el.innerHTML =
      '<div class="mb-replaced-banner__content mb-qv-added-banner">' +
      '<img class="mb-replaced-banner__thumb" src="' + utils.escapeHtml(pending.fromItem.productImage || '') + '" alt="">' +
      '<span class="mb-replaced-banner__text"><strong>Replacing</strong> - ' + utils.escapeHtml(pending.fromItem.productTitle) + '</span>' +
      '<span class="mb-replaced-banner__arrow" aria-hidden="true">&rarr;</span>' +
      '<img class="mb-replaced-banner__thumb" src="' + utils.escapeHtml(pending.toItemValue.productImage || '') + '" alt="">' +
      '<span class="mb-replaced-banner__text">' + utils.escapeHtml(pending.toItemValue.productTitle) + '</span>' +
      '</div>' +
      renderReplaceCompareDesktop(pending, buttonReplace) +
      '<button type="button" class="mb-btn mb-btn--primary mb-btn--block view-bundle mb-visible" data-mb-action="cancel-replace">' +
      '<span class="mb-btn__label">&larr; View Multi Buy Pack</span>' +
      '</button>';
  }

  /**
   * Desktop-only "Current Product / New Product" comparison card - hidden
   * on mobile via CSS (.mb-replace-compare), which keeps showing the
   * existing compact .mb-replaced-banner__content row above exactly as
   * before (that one is hidden on desktop instead - see multibuy.css).
   * Shares the same CTA button (rendered once, right after both).
   */
  function renderReplaceCompareDesktop(pending, buttonReplace) {
    function itemHtml(label, item) {
      return (
        '<div class="mb-replace-compare__item">' +
        '<img class="mb-replace-compare__thumb" src="' + utils.escapeHtml(item.productImage || '') + '" alt="">' +
        '<div class="mb-replace-compare__text">' +
        '<span class="mb-replace-compare__label">' + utils.escapeHtml(label) + '</span>' +
        '<p class="mb-replace-compare__title">' + utils.escapeHtml(item.productTitle) + '</p>' +
        '<p class="mb-replace-compare__meta">' +
        (item.variantTitle ? 'Size ' + utils.escapeHtml(item.variantTitle) + ' &middot; ' : '') +
        'Qty ' + item.quantity +
        '</p>' +
        '</div>' +
        '</div>'
      );
    }
    return (
      '<div class="mb-replace-compare">' +
      itemHtml('Current Product', pending.fromItem) +
      '<span class="mb-replace-compare__arrow" aria-hidden="true">&rarr;</span>' +
      itemHtml('New Product', pending.toItemValue) +
      buttonReplace +
      '</div>'
    );
  }

  /**
   * getProducts/onSelectProduct are optional - when supplied, wires the
   * shared live-suggestions dropdown (see bindSearchSuggestions) onto
   * this toolbar's search input too.
   */
  function bindToolbar(toolbarEl, onSearch, onFilter, onSort, getProducts, onSelectProduct) {
    if (!toolbarEl) return;
    toolbarEl.innerHTML =
      '<div class="mb-search-wrap">' +
      '<input type="search" class="mb-input" data-mb-search placeholder="Search" autocomplete="off">' +
      '<button type="button" class="mb-search-clear" data-mb-search-clear hidden aria-label="Clear search">&times;</button>' +
      '<img class="mb-search-icon" data-mb-search-icon src="https://cdn.shopify.com/s/files/1/0589/9268/9284/files/search.svg?v=1784367077" alt="" aria-hidden="true">' +
      '</div>' +
      '<select class="mb-select" data-mb-sort>' +
      '<option value="featured">Featured</option>' +
      '<option value="newest">Newest</option>' +
      '<option value="alphabetical">Alphabetical</option>' +
      '<option value="price-asc">Price: Low to High</option>' +
      '<option value="price-desc">Price: High to Low</option>' +
      '</select>' +
      '<div class="mb-search-suggestions" data-mb-search-suggestions hidden></div>';
    var searchInput = utils.qs('[data-mb-search]', toolbarEl);
    var debouncedSearch = utils.debounce(function () { onSearch(searchInput.value); }, 250);
    searchInput.addEventListener('input', debouncedSearch);
    utils.qs('[data-mb-sort]', toolbarEl).addEventListener('change', function (e) {
      onSort(e.target.value);
    });
    if (getProducts) {
      bindSearchSuggestions(searchInput, getProducts, onSelectProduct);
    }
  }

  /**
   * Live search suggestions + clear (x) button, shared by every search
   * input in the app (Create Your Own Pack toolbar and the bottom
   * sheet's toolbar). Matching itself reuses MB.search.searchProducts -
   * no separate search implementation. Selecting a suggestion fills the
   * input with that product's title (so the grid narrows to it via the
   * input's own existing debounced "input" handler) and calls onSelect,
   * which callers wire to the existing Quick View drawer.
   */
  function bindSearchSuggestions(inputEl, getProducts, onSelect) {
    if (!inputEl) return;
    var wrap = inputEl.closest('.mb-search-wrap');
    if (!wrap) return;
    // The dropdown lives one level up, as a sibling of the search box
    // (not nested inside it), so it can span the full toolbar row -
    // search + filter + sort - rather than just the search input's width.
    var rowEl = inputEl.closest('.mb-toolbar__controls, .mb-sheet__toolbar') || wrap;
    var clearBtn = utils.qs('[data-mb-search-clear]', wrap);
    var searchIcon = utils.qs('[data-mb-search-icon]', wrap);
    var dropdown = utils.qs('[data-mb-search-suggestions]', rowEl);
    if (!dropdown) return;

    function updateClearVisibility() {
      var hasValue = !!inputEl.value;
      if (clearBtn) clearBtn.hidden = !hasValue;
      if (searchIcon) searchIcon.hidden = hasValue;
    }

    function hideDropdown() {
      dropdown.hidden = true;
      dropdown.innerHTML = '';
    }

    function renderSuggestions(products) {
      if (!products.length) {
        hideDropdown();
        return;
      }
      dropdown.innerHTML = products
        .slice(0, 6)
        .map(function (p) {
          return (
            '<button type="button" class="mb-search-suggestion" data-product-id="' + p.id + '">' +
            '<img src="' + utils.escapeHtml(p.featured_image || '') + '" alt="' + utils.escapeHtml(p.title) + '" loading="lazy">' +
            '<span class="mb-search-suggestion__title">' + utils.escapeHtml(p.title) + '</span>' +
            '</button>'
          );
        })
        .join('');
      dropdown.hidden = false;
    }

    var debouncedSuggest = utils.debounce(function () {
      updateClearVisibility();
      var query = inputEl.value.trim();
      if (!query) {
        hideDropdown();
        return;
      }
      renderSuggestions(MB.search.searchProducts(getProducts(), query));
    }, 200);

    inputEl.addEventListener('input', debouncedSuggest);
    inputEl.addEventListener('focus', function () {
      if (inputEl.value.trim()) debouncedSuggest();
    });

    dropdown.addEventListener('click', function (e) {
      var btn = e.target.closest('.mb-search-suggestion');
      if (!btn) return;
      var product = getProducts().filter(function (p) { return String(p.id) === btn.dataset.productId; })[0];
      hideDropdown();
      if (!product) return;
      inputEl.value = product.title;
      updateClearVisibility();
      inputEl.dispatchEvent(new Event('input', { bubbles: true }));
      if (onSelect) onSelect(product);
    });

    document.addEventListener('click', function (e) {
      if (!rowEl.contains(e.target)) hideDropdown();
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        inputEl.value = '';
        updateClearVisibility();
        hideDropdown();
        inputEl.dispatchEvent(new Event('input', { bubbles: true }));
        inputEl.focus();
      });
    }

    updateClearVisibility();
  }

  function bindSheetEvents() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-mb-action="sheet-close"]')) {
        close();
      }
      var cancelBtn = e.target.closest('[data-mb-action="cancel-replace"]');
      if (cancelBtn) {
        // Back arrow or the "View Multi Buy Pack" CTA - the only two
        // places a pending pick actually gets written to the pack.
        completeReplacement(sheet.bundleId);
      }
    });

    document.addEventListener('multibuy:sheet:open', function (e) {
      open(e.detail.bundleId);
    });

    store.on('bundle:changed', function (ctx) {
      if (sheet.bundleId === ctx.id) {
        renderSheetPricing(ctx);
        renderNextTierBanner(ctx);
      }
    });
  }

  MB.collection = {
    stageReplacement: stageReplacement,
    getPendingReplacement: getPendingReplacement,
    completeReplacement: completeReplacement,
    createGridController: createGridController,
    bindGridEvents: bindGridEvents,
    bindSheetEvents: bindSheetEvents,
    bindToolbar: bindToolbar,
    bindSearchSuggestions: bindSearchSuggestions,
    open: open,
    close: close,
    openWithContent: openWithContent,
    refreshCustomContent: refreshCustomContent,
    isShowingCustomContent: isShowingCustomContent
  };
})(window, document);
